import { type UseQueryResult, useQueries, useQuery } from "@tanstack/react-query"
import { Landmark, Lock, Pencil, Plus } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { AvisoCuentaBloqueada, esCuentaBloqueada } from "@/componentes/tesoreria/CuentaBloqueada"
import { DialogoCuenta } from "@/componentes/tesoreria/DialogoCuenta"
import { DialogoPin } from "@/componentes/tesoreria/DialogoPin"
import { aCentimos, comoMonto, monto } from "@/componentes/tesoreria/dinero"
import { FichaCuenta } from "@/componentes/tesoreria/FichaCuenta"
import { Terminacion } from "@/componentes/tesoreria/Terminacion"
import type { Cuenta, Divisa, ResumenCuenta } from "@/componentes/tesoreria/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * Bancos, con la estructura del módulo de len-platform: las acciones arriba a la
 * derecha, los totales en tarjetas, las cuentas a la izquierda y la elegida a la derecha.
 *
 * ⚠ **La estructura sí; el negocio no.** Allí las tarjetas son saldo, «congelado para
 * impuestos» y disponible, y hay «Transferir», conciliación y apartados: son de otro
 * modelo de negocio y aquí no existen. Aquí las tarjetas son el saldo de cada divisa.
 *
 * ⚠ **Estado de cuenta, no panel de cuentas.** Con las cuentas en fila encima del
 * extracto, cinco o seis envolvían y empujaban la tabla fuera de la pantalla, y cada
 * cambio de cuenta obligaba a subir, elegir y volver a bajar. En columna se ven todas
 * a la vez con su saldo —que es como se decide de cuál pagar— y el extracto no se
 * mueve del sitio.
 *
 * ⚠ **La primera cuenta se elige sola.** Entrar y tener que elegir antes de ver nada
 * es un paso de más en cada visita.
 *
 * ⚠ **Los totales van POR DIVISA, nunca sumados entre ellas.** Hay cuentas en
 * bolívares, dólares, euros y USDT; sumarlas exigiría convertir, y un total convertido
 * cambia cada vez que se mueve la tasa sin que se haya movido un céntimo.
 *
 * ⚠ **Del número de cuenta sólo sale la terminación** —«···4521», lo que enseña
 * cualquier banco— y **el banco es opcional**: el efectivo en caja y una billetera no
 * tienen.
 */
export function Bancos() {
  const usuario = usarSesion((e) => e.usuario)
  const puedeAdministrar = tienePermiso(usuario, "tesoreria:administrar")
  const [elegida, setElegida] = useState<number | null>(null)
  const [verInactivas, setVerInactivas] = useState(false)
  const [creando, setCreando] = useState(false)
  const [editando, setEditando] = useState<Cuenta | null>(null)
  // La cuenta cuyo PIN se está pidiendo. Se sabe que hace falta porque el
  // servidor responde `CUENTA_BLOQUEADA` al pedir su resumen: no hay una segunda
  // consulta que preguntarle si está abierta.
  const [pidiendoPin, setPidiendoPin] = useState<Cuenta | null>(null)

  const cuentas = useQuery({
    queryKey: ["cuentas-bancarias"],
    queryFn: () => api.get<Cuenta[]>("/api/v1/cuentas-bancarias"),
  })
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
  })

  const todas = cuentas.data ?? []
  const lista = todas.filter((c) => verInactivas || c.activo)
  const resumenes = useQueries({
    queries: lista.map((c) => ({
      queryKey: ["resumen-cuenta", c.id],
      queryFn: () => api.get<ResumenCuenta>(`/api/v1/cuentas-bancarias/${c.id}/resumen`),
    })),
  })
  const resumenDe = new Map(lista.map((c, i) => [c.id, resumenes[i]]))
  const codigo = new Map((divisas.data ?? []).map((d) => [d.id, d.codigo]))
  // Derivada y no guardada: si la elegida se oculta al quitar las inactivas, se cae a
  // la primera sin un efecto que lo arregle un render tarde.
  const actual = lista.find((c) => c.id === elegida) ?? lista[0] ?? null
  const totales = totalesPorDivisa(
    lista.filter((c) => c.activo),
    resumenDe,
    codigo,
  )

  return (
    <div>
      {/* Ni título ni frase propios: los pone la cabecera de «Tesorería». Aquí arriba
          sólo van las acciones, a la derecha. */}
      {(puedeAdministrar || lista.length > 0) && (
        <div className="mb-4 flex flex-wrap items-start justify-end gap-2">
          {lista.length > 0 && (
            <Exportar
              titulo="Cuentas bancarias"
              etiqueta="Exportar cuentas"
              subtitulo={
                verInactivas ? "Todas las cuentas, también las inactivas" : "Cuentas activas"
              }
              documento={() => documentoDeCuentas(lista, totales, resumenDe, codigo, verInactivas)}
            />
          )}
          {puedeAdministrar && (
            <Boton variante="primario" onClick={() => setCreando(true)}>
              <Plus size={16} aria-hidden={true} />
              Nueva cuenta
            </Boton>
          )}
        </div>
      )}

      {cuentas.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}
      {cuentas.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudieron cargar las cuentas
        </p>
      )}
      {cuentas.data?.length === 0 && (
        <div className="tarjeta flex flex-col items-center gap-2 py-12 text-center">
          <Landmark size={22} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
          <p className="text-sm font-medium">Todavía no hay cuentas bancarias</p>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Sin una cuenta no hay dónde registrar un movimiento: el dinero tiene que estar en algún
            sitio.
          </p>
        </div>
      )}

      {todas.length > 0 && (
        <>
          {totales.length > 0 && (
            <ul
              aria-label="Saldo por divisa"
              className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              {totales.map((t) => (
                <li key={t.divisa} className="tarjeta">
                  <p
                    className="text-[11px] font-semibold tracking-wide uppercase"
                    style={{ color: "var(--fg-muted)" }}
                  >
                    Saldo total
                  </p>
                  <p
                    className="tabular mt-1 text-2xl font-semibold"
                    style={{
                      color: t.total !== null && t.total < 0n ? "var(--destructive)" : undefined,
                    }}
                  >
                    {t.total === null ? "…" : comoMonto(t.total)}
                    <span className="ml-1 text-xs font-normal" style={{ color: "var(--fg-muted)" }}>
                      {t.divisa}
                    </span>
                  </p>
                  <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                    {t.cuentas} cuenta{t.cuentas === 1 ? "" : "s"} activa
                    {t.cuentas === 1 ? "" : "s"}
                  </p>
                </li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)]">
            <nav aria-label="Cuentas bancarias" className="min-w-0">
              <p className="rotulo mb-2">Cuentas</p>
              {lista.length === 0 ? (
                <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                  Todas las cuentas están inactivas.
                </p>
              ) : (
                <ul className="grid gap-1.5">
                  {lista.map((c) => {
                    const seleccionada = actual?.id === c.id
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          aria-pressed={seleccionada}
                          onClick={() => {
                            setElegida(c.id)
                            if (estaBloqueada(resumenDe.get(c.id))) setPidiendoPin(c)
                          }}
                          data-testid={`cuenta-${c.id}`}
                          // El vocabulario del cristal, como el menú y las pestañas: la
                          // elegida sube la luz, las demás llevan un filo apenas visible.
                          className={`foco-cristal w-full rounded-[var(--radius-tarjeta)] px-3 py-2.5 text-left transition-colors ${
                            seleccionada ? "activo-cristal" : "toque-cristal"
                          }`}
                          style={{
                            boxShadow: seleccionada
                              ? undefined
                              : "inset 0 0 0 1px rgb(var(--marco-tinta) / 0.08)",
                            opacity: c.activo ? 1 : 0.55,
                          }}
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span className="truncate text-sm font-medium">{c.nombre}</span>
                            {!c.activo && <Insignia tono="neutro">Inactiva</Insignia>}
                          </span>
                          {/* Abajo, la terminación y el saldo, como en len-platform; el
                              banco va delante de la terminación porque aquí es un dato
                              aparte del nombre. */}
                          <span className="mt-0.5 flex items-baseline justify-between gap-2">
                            <span
                              className="min-w-0 truncate text-xs"
                              style={{ color: "var(--fg-muted)" }}
                            >
                              {c.banco ?? "Sin banco"} <Terminacion ultimos4={c.ultimos4} />
                            </span>
                            {estaBloqueada(resumenDe.get(c.id)) ? (
                              <span
                                className="flex shrink-0 items-center gap-1 text-xs"
                                style={{ color: "var(--fg-muted)" }}
                              >
                                <Lock size={12} aria-hidden={true} /> Con PIN
                              </span>
                            ) : (
                              <SaldoEnLista
                                consulta={resumenDe.get(c.id)}
                                divisa={codigo.get(c.divisa_id) ?? ""}
                              />
                            )}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}

              {todas.some((c) => !c.activo) && (
                <label
                  className="mt-2 flex items-center gap-2 px-1 text-xs"
                  style={{ color: "var(--fg-muted)" }}
                >
                  <input
                    type="checkbox"
                    checked={verInactivas}
                    onChange={(e) => setVerInactivas(e.target.checked)}
                  />
                  Ver las inactivas
                </label>
              )}
            </nav>

            <div className="min-w-0">
              {actual && estaBloqueada(resumenDe.get(actual.id)) ? (
                <AvisoCuentaBloqueada
                  nombre={actual.nombre}
                  onAbrir={() => setPidiendoPin(actual)}
                />
              ) : (
                actual && (
                  <FichaCuenta
                    key={actual.id}
                    cuenta={actual}
                    divisa={codigo.get(actual.divisa_id) ?? ""}
                  />
                )
              )}
              {actual && puedeAdministrar && (
                <div className="mt-3 flex justify-end">
                  <Boton tamano="sm" onClick={() => setEditando(actual)}>
                    <Pencil size={14} aria-hidden={true} /> Editar cuenta
                  </Boton>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <DialogoCuenta
        abierto={creando || editando !== null}
        cuenta={editando}
        onCerrar={() => {
          setCreando(false)
          setEditando(null)
        }}
        onCreada={(c) => setElegida(c.id)}
      />
      <DialogoPin cuenta={pidiendoPin} onCerrar={() => setPidiendoPin(null)} />
    </div>
  )
}

/**
 * Si esa cuenta está cerrada para esta sesión.
 *
 * ⚠ Sale del ERROR de su resumen y no de una consulta aparte: el servidor ya dice
 * `CUENTA_BLOQUEADA` al negarlo, y preguntar dos veces lo mismo deja las dos
 * respuestas pudiendo discrepar.
 */
function estaBloqueada(consulta: UseQueryResult<ResumenCuenta> | undefined): boolean {
  return esCuentaBloqueada(consulta?.error)
}

function SaldoEnLista({
  consulta,
  divisa,
}: {
  consulta: UseQueryResult<ResumenCuenta> | undefined
  divisa: string
}) {
  if (consulta === undefined || consulta.isPending) {
    return (
      <span className="tabular text-sm" style={{ color: "var(--fg-muted)" }}>
        …
      </span>
    )
  }
  if (consulta.isError) {
    // Un cero se leería como «esta cuenta está vacía», que es otra afirmación.
    return (
      <span className="shrink-0 text-xs" style={{ color: "var(--destructive)" }}>
        sin dato
      </span>
    )
  }
  const negativo = aCentimos(consulta.data.saldo) < 0n
  return (
    <span
      className="tabular shrink-0 text-sm font-semibold"
      style={{ color: negativo ? "var(--destructive)" : undefined }}
    >
      {monto(consulta.data.saldo)}
      <span className="ml-1 text-[11px] font-normal" style={{ color: "var(--fg-muted)" }}>
        {divisa}
      </span>
    </span>
  )
}

/**
 * El saldo de las cuentas activas, sumado por divisa. `null` mientras falte alguno:
 * un total con una cuenta de menos es un número equivocado que parece bueno.
 */
function totalesPorDivisa(
  cuentas: readonly Cuenta[],
  resumenDe: Map<number, UseQueryResult<ResumenCuenta> | undefined>,
  codigo: Map<number, string>,
): { divisa: string; cuentas: number; total: bigint | null }[] {
  const grupos = new Map<string, { cuentas: number; total: bigint | null }>()
  for (const c of cuentas) {
    const clave = codigo.get(c.divisa_id) ?? "—"
    const previo = grupos.get(clave) ?? { cuentas: 0, total: 0n }
    const resumen = resumenDe.get(c.id)?.data
    grupos.set(clave, {
      cuentas: previo.cuentas + 1,
      total:
        previo.total === null || resumen === undefined
          ? null
          : previo.total + aCentimos(resumen.saldo),
    })
  }
  return [...grupos].map(([divisa, g]) => ({ divisa, ...g }))
}

/**
 * Las cuentas de la lista de la izquierda, con su saldo, y el total por divisa.
 *
 * ⚠ La lista son botones, no una tabla: se arma aquí con lo que ya enseña la pantalla.
 * Una cuenta con PIN cerrado sale SIN saldo y lo dice: exportar no abre lo que la
 * pantalla no enseña.
 */
function documentoDeCuentas(
  lista: readonly Cuenta[],
  totales: { divisa: string; cuentas: number; total: bigint | null }[],
  resumenDe: Map<number, UseQueryResult<ResumenCuenta> | undefined>,
  codigo: Map<number, string>,
  verInactivas: boolean,
): DocumentoExportable {
  return {
    titulo: "Cuentas bancarias",
    subtitulo: verInactivas ? "Todas las cuentas, también las inactivas" : "Cuentas activas",
    secciones: [
      {
        titulo: "Saldo por divisa",
        columnas: [
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Cuentas activas", tipo: "numero" },
          { titulo: "Saldo total", tipo: "monto" },
        ],
        filas: totales.map((t) => ({
          celdas: [t.divisa, t.cuentas, t.total === null ? null : Number(t.total) / 100],
        })),
      },
      {
        titulo: "Cuentas",
        columnas: [
          { titulo: "Cuenta", tipo: "texto" },
          { titulo: "Banco", tipo: "texto" },
          { titulo: "Terminación", tipo: "texto" },
          { titulo: "Estado", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Saldo", tipo: "monto" },
        ],
        filas: lista.map((c) => {
          const consulta = resumenDe.get(c.id)
          const bloqueada = estaBloqueada(consulta)
          const saldo = consulta?.data ? Number(consulta.data.saldo) : null
          return {
            celdas: [
              c.nombre,
              c.banco ?? "Sin banco",
              c.ultimos4 ? `···${c.ultimos4}` : null,
              [c.activo ? "Activa" : "Inactiva", bloqueada ? "con PIN, sin saldo a la vista" : ""]
                .filter(Boolean)
                .join(", "),
              codigo.get(c.divisa_id) ?? null,
              bloqueada ? null : saldo,
            ],
          }
        }),
      },
    ],
  }
}
